import React, { useState, useCallback, useEffect, useRef } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";

interface Option {
  value: string;
  label: string;
}

interface SingleSelectDropdownProps {
  options: Option[];
  selectedValue: string | "";
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  theme?: 'light' | 'dark';
}

const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder,
  className = "",
  theme = 'dark',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const resolvedPlaceholder = placeholder || t("selection.select");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredOptions = searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const getDisplayText = useCallback(() => {
    if (!selectedValue) {
      return resolvedPlaceholder;
    }
    const selected = options.find((opt) => opt.value === selectedValue);
    return selected ? selected.label : resolvedPlaceholder;
  }, [selectedValue, options, resolvedPlaceholder]);

  const clearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange("");
    },
    [onChange]
  );

  // Theme-based styling
  const themeStyles = {
    light: {
      container: "w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-lg cursor-pointer h-[50px] focus:ring-2 focus:ring-orange-500 focus:border-transparent",
      placeholderText: "text-gray-500",
      selectedText: "text-gray-900",
      clearButton: "mr-2 text-gray-400 hover:text-gray-600",
      chevron: "w-5 h-5 ml-2 text-gray-400"
    },
    dark: {
      container: "w-full px-4 py-3 bg-black bg-opacity-50 text-white border border-gray-600 rounded-md cursor-pointer h-[50px]",
      placeholderText: "text-gray-400",
      selectedText: "text-white",
      clearButton: "mr-2 text-gray-400 hover:text-white",
      chevron: "w-5 h-5 ml-2"
    }
  };

  const currentTheme = themeStyles[theme];

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      {/* Selected value display */}
      <div
        className={currentTheme.container}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex justify-between items-center h-full">
          <div
            className={`truncate max-w-[75%] ${
              !selectedValue ? currentTheme.placeholderText : currentTheme.selectedText
            }`}
          >
            {getDisplayText()}
          </div>
          <div className="flex items-center flex-shrink-0">
            {selectedValue && (
              <button
                onClick={(e) => clearSelection(e)}
                className={currentTheme.clearButton}
                aria-label={t("selection.clear")}
              >
                <MdClose size={18} />
              </button>
            )}
            <svg
              className={currentTheme.chevron}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white rounded-md shadow-lg max-h-[300px] flex flex-col">
          {/* Search input */}
          <div className="sticky top-0 bg-white p-2 border-b z-10">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-700 h-[40px]"
              placeholder={t("selection.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="py-2 overflow-y-auto flex-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 flex items-center justify-between hover:bg-orange-50 cursor-pointer ${
                    selectedValue === option.value ? "bg-orange-50" : ""
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  <span className="text-gray-700 truncate">{option.label}</span>
                  {selectedValue === option.value && (
                    <MdCheck className="text-orange-500 flex-shrink-0" size={18} />
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 italic">
                {t("selection.noResults")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleSelectDropdown;
